import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Container from "@material-ui/core/Container";
import Grid from "@material-ui/core/Grid";
import Sidebar from "../../sidebar/sidebar";
import MaterialTable from "@material-table/core";
import {
  useStyles,
  useStylesForMaterial,
  toolbarStyles,
  materialTableStyle,
  paginationStyle,
} from "../../reusable/styles";
import { useTranslation } from "react-i18next";
import Pagination from "../../reusable/pagination";
import { useDispatch } from "react-redux";
import { optionsGeneral } from "../../reusable/optionsForTables";
import {
  disableUserBySysadmin,
  enableUserBySysadmin,
  getUsersPage,
  requestPasswordResetBySysadmin,
} from "../../actions/users";
import Style from "./users.module.css";
import { UsersColumns } from "../../columns/userColumns";
import MagnifyingGlass from "../../common/assets/magnifying-glass.svg";
import ConfirmationModal from "../../common/modal/confirmationModal";
import { createUsersRefreshScheduler } from "./usersRefresh";
import GridProgress from "../../reusable/gridProgress";
import LiveRegion from "../../reusable/liveRegion";
import useDelayedBusy from "../../utils/useDelayedBusy";

// Long enough that typing a name straight through issues one request rather
// than one per keystroke, short enough not to feel like lag after a pause.
export const USERS_FILTER_DEBOUNCE_MS = 400;

export default function Users() {
  const { t } = useTranslation();
  const classes = useStyles();
  const classesMaterial = useStylesForMaterial();

  const classesForm = toolbarStyles();
  const dispatch = useDispatch();
  const [height, setHeight] = useState(window.innerHeight);
  const [confirmationDisableUser, setConfirmationDisableUser] = useState(false);
    const [confirmationResetPassword, setConfirmationResetPassword] = useState(false);
  const [inactiveListVisible, setInactiveListVisible] = useState(false);
  const [userIDForArchive, setUserIDForArchive] = useState("");
    const [userEmailForPasswordReset, setUserEmailForPasswordReset] = useState("");
  const [isUsersRefreshPending, setIsUsersRefreshPending] = useState(false);
  const [isGridLoading, setIsGridLoading] = useState(false);
  // Two flavours of busy end up in the same place: a query is in flight, or the
  // list is settling after an activation while a filter is on. Both mean the
  // rows on screen are about to be replaced.
  const isGridBusy = isGridLoading || isUsersRefreshPending;
  // The block reads the real flag so nothing can be acted on mid replacement;
  // the dimming and the bar read the delayed one so a fast query does not
  // strobe the table. See the note on useDelayedBusy.
  const showGridProgress = useDelayedBusy(isGridBusy, { delay: 150 });
  const tableRef = useRef();
  const gridRef = useRef();
  const inactiveListVisibleRef = useRef(inactiveListVisible);
  const refreshSchedulerRef = useRef();
  const deferredRefreshRequestRef = useRef(false);
  const isMountedRef = useRef(true);
  const requestSeqRef = useRef(0);
  const latestRequestRef = useRef(null);

  if (!refreshSchedulerRef.current) {
    refreshSchedulerRef.current = createUsersRefreshScheduler({
      getTable: () => tableRef.current,
      getInactiveListVisible: () => inactiveListVisibleRef.current,
      onDeferredRefreshStart: () => {
        deferredRefreshRequestRef.current = true;
      },
      onWaitingChange: (isWaiting) => {
        if (isMountedRef.current) {
          setIsUsersRefreshPending(isWaiting);
        }
      },
    });
  }

  const handleResize = () => {
    setHeight(window.innerHeight);
  };

  useEffect(() => {
    window.addEventListener("resize", handleResize, false);
  }, []);

  useEffect(() => {
    inactiveListVisibleRef.current = inactiveListVisible;
    refreshSchedulerRef.current.cancel();
  }, [inactiveListVisible]);

  useEffect(
    () => () => {
      isMountedRef.current = false;
      refreshSchedulerRef.current.cancel();
    },
    []
  );

  // pointer-events alone still leaves the row action toggles reachable by Tab,
  // so the data rows are marked inert as well. Per row rather than on tbody:
  // MaterialTable puts the filter row in the body too, and making that inert
  // blurs the input mid keystroke. The filter row is the one body row carrying
  // an input. Runs on every render because the rows themselves are replaced
  // when a page of data arrives.
  useEffect(() => {
    gridRef.current?.querySelectorAll("tbody tr").forEach((row) => {
      if (row.querySelector("input")) {
        return;
      }
      if (isGridBusy) {
        row.setAttribute("inert", "");
      } else {
        row.removeAttribute("inert");
      }
    });
  });

  const loadUsers = useCallback(
    (query) => {
      const isDeferredRefreshRequest = deferredRefreshRequestRef.current;
      const seq = ++requestSeqRef.current;

      setIsGridLoading(true);

      const request = getUsersPage(inactiveListVisible, query)
        .then((result) => {
          // The filter row stays live while a request is in flight, so a slow
          // early response can land after a later one. Rather than painting a
          // page the operator has already typed past, a superseded request
          // settles on whatever the newest one returns - the chain always ends
          // at the request that matches what is in the filter box.
          if (seq !== requestSeqRef.current) {
            return latestRequestRef.current;
          }
          return result;
        })
        .finally(() => {
          if (isDeferredRefreshRequest) {
            deferredRefreshRequestRef.current = false;
            refreshSchedulerRef.current.complete();
          }
          if (seq === requestSeqRef.current && isMountedRef.current) {
            setIsGridLoading(false);
          }
        });

      latestRequestRef.current = request;
      return request;
    },
    [inactiveListVisible]
  );

  const refreshUsers = () => {
    refreshSchedulerRef.current.refresh();
  };

  const disableUser = (id) => {
    dispatch(
      disableUserBySysadmin(
        id,
        setConfirmationDisableUser,
        setUserIDForArchive,
        refreshUsers
      )
    );
  };

  const enableUser = (id) => {
    dispatch(
      enableUserBySysadmin(
        id,
        setConfirmationDisableUser,
        setUserIDForArchive,
        refreshUsers
      )
    );
  };

  const resetPassword = (email) => {
    dispatch(
        requestPasswordResetBySysadmin(email, setConfirmationResetPassword, setUserEmailForPasswordReset)
    );
  }

  // MaterialTable reads components as element types and deep compares columns
  // and options on every update, so all three have to keep a stable identity.
  // Recreating them per render remounts the paginator and, worse, drops the
  // active filter: setColumns carries filterValue over from the previous
  // columns array by looking up columnDef.id, which these columns do not carry,
  // so a fresh array falls back to defaultFilter. This only started to matter
  // once the loading flag below began re-rendering on every query.
  const components = useMemo(
    () => ({
      Toolbar: () => (
        <div className={classesForm.root}>
          <div className={Style.header}>
            <div className={Style.header__text}>{t("Sidebar.Users")}</div>
            <button
              className="addEntityButton"
              onClick={() => {
                setInactiveListVisible(!inactiveListVisible);
              }}
            >
              <p className={Style.header__addButtonText}>
                {inactiveListVisible
                  ? t("Users.ActiveUsers")
                  : t("Users.InactiveUsers")}
              </p>
            </button>
          </div>
        </div>
      ),
      Pagination: (props) => (
        <td style={paginationStyle}>
          <Pagination translate={t} {...props} classes={{ ...classesMaterial }} />
        </td>
      ),
      // The built in overlay is a 70% opaque sheet over the whole table, filter
      // row included, on every single query - which is what made searching feel
      // like the grid kept blinking out. The bar plus the dimmed body below
      // report the same thing without hiding the results being searched.
      OverlayLoading: () => null,
    }),
    [classesForm, classesMaterial, inactiveListVisible, t]
  );

  const columns = useMemo(
    () =>
      UsersColumns(
        t,
        setConfirmationDisableUser,
        setUserIDForArchive,
        inactiveListVisible,
        inactiveListVisible,
        setConfirmationResetPassword,
        setUserEmailForPasswordReset
      ),
    [inactiveListVisible, t]
  );

  const options = useMemo(
    () => ({
      ...optionsGeneral(height - 240, 44),
      filtering: true,
      sorting: false,
      // Every keystroke in the filter row hits the server, so this has to be
      // set explicitly. MaterialTable builds its filter debounce in the
      // constructor from the raw options prop, and React's defaultProps only
      // fills in a wholly missing prop - so the documented 200ms default never
      // reaches a table that passes an options object, and the debounce package
      // falls back to its own 100ms.
      debounceInterval: USERS_FILTER_DEBOUNCE_MS,
    }),
    [height]
  );

  return (
    <div className={classes.root}>
      <>
        <Sidebar />
        <main className={classes.content}>
          <div className={classes.appBarSpacer} />

          <Container maxWidth={false} className={classes.container}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <div className="text-center">
                  <div className="row content">
                    <div className="col-sm-12">
                      <div
                        ref={gridRef}
                        className={[
                          classes.paper,
                          isGridBusy ? Style.gridBlocked : "",
                          showGridProgress ? Style.gridDimmed : "",
                        ]
                          .filter(Boolean)
                          .join(" ")}
                        aria-busy={isGridBusy}
                      >
                        <LiveRegion
                          message={
                            isGridBusy ? t("MaterialTable.loadingRows") : ""
                          }
                        />
                        <GridProgress busy={showGridProgress} />
                        <MaterialTable
                          key={inactiveListVisible ? "inactive-users" : "active-users"}
                          tableRef={tableRef}
                          style={materialTableStyle}
                          icons={{
                            Filter: () => (
                              <img src={MagnifyingGlass} alt="search" />
                            ),
                          }}
                          data={loadUsers}
                          onFilterChange={(filters) => {
                            if (filters.length === 0) {
                              refreshSchedulerRef.current.cancel();
                            }
                          }}
                          columns={columns}
                          options={options}
                          components={components}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </Grid>
            </Grid>
          </Container>
        </main>
      </>
      {confirmationDisableUser && (
        <ConfirmationModal
          title={
            inactiveListVisible
              ? t("Users.ActivateUsers")
              : t("Users.DeactivateUsers")
          }
          message={
            inactiveListVisible
              ? t("Users.ActivateUserMessage")
              : t("Users.DeactivateUserMessage")
          }
          confirmationButtonText={
            inactiveListVisible ? t("Users.Active") : t("Users.Deactive")
          }
          open={confirmationDisableUser}
          onClose={() => {
            setConfirmationDisableUser(false);
            setUserIDForArchive("");
          }}
          confirmationAction={() => {
            inactiveListVisible
              ? enableUser(userIDForArchive)
              : disableUser(userIDForArchive);
          }}
          width="450px"
        />
      )}
      {confirmationResetPassword && (
        <ConfirmationModal
          title={t("Users.ResetPassword")}
          message={t("Users.ResetPasswordMessage")}
          confirmationButtonText={t("Users.Reset")}
          open={confirmationResetPassword}

          onClose={() => {
            setConfirmationResetPassword(false);
            setUserEmailForPasswordReset("");
          }}
          confirmationAction={() => {
            resetPassword(userEmailForPasswordReset);
            console.log("Reset password for user with email: ", userEmailForPasswordReset);
          }}
          width="450px"
        />
        )}
    </div>
  );
}
